/**
 * @description 显示所有真实数据组件
 * @date 2025-01-17
 * @author lv.chen
 */

import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useSelector } from 'umi';
import { Table, Modal, Button } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import InfoIconTip, {
  closeTipGroup,
} from '@intelligence/components/InfoIconTip';
import Loading from '@intelligence/components/Loading';
import ImagePreview from '@intelligence/components/ImagePreview';
import PublicTrueData, { IPublicTrueDataBtnLogConfig } from '@intelligence/components/PublicTrueData';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import useWindowResize from './hooks/useWindowResize';
import useTablePagination from './hooks/useTablePagination';
import useTableSort from './hooks/useTableSort';
import useImagePreview from './hooks/useImagePreview';
import useDeleteConfirm from './hooks/useDeleteConfirm';
import useFeedbackEdit from './hooks/useFeedbackEdit';
import useFormattedTableData from './hooks/useFormattedTableData';
import useTableColumns from './hooks/useTableColumns';
import useTrueDataFeedbackData from './hooks/useTrueDataFeedbackData';
import useTrueDataDownload from './hooks/useTrueDataDownload';
import { markMetricGroups } from './utils';
import { notificationSimple } from '@intelligence/utils/commonUtils';
import { reportButtonLog } from '@intelligence/v3/utils/logReporter';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { getAlineaMetricItem } from '@intelligence/v3/pages/TopCharts/PCConsoleGames/AlineaAnalyticsCalibration/utils';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import AlineaActiveIcon from '@intelligence/assets/svg/dataSource/alinea-active.svg';

import type { IPublicTrueData, IPublicFeedback } from '@intelligence/types/topCharts';
import type { IRegion } from '@intelligence/types/common';
import type { ITrueDataHeaderLabels, TTrueDataMode, TTrueDataSourceLabels } from './types';

import './index.scss';

const { scrollable, autotip, pageable, injectable } = Table.addons;

const PAGE_SIZE = 100;

// 与 Mobile 品类筛选器保持同一组外部兜底：离开标题区域或关闭弹窗时强制清理 InfoIcon 状态。
const TRUE_DATA_INFO_TIP_GROUP = 'true-data-info-tip';

interface IShowAllTrueDataState {
  home?: {
    userInfo?: {
      userid?: number;
    };
  };
}

interface IShowAllTrueDataProps extends IBaseProps, IPublicTrueDataBtnLogConfig, IPublicTrueData {
  gameName?: string;
  regionsCountries?: IRegion[];
}

const ShowAllTrueData: React.FC<IShowAllTrueDataProps> = (props) => {
  const {
    style,
    className,
    pageId,
    entityType,
    gameId,
    gameData,
    setGameData,
    selectGameName,
    setSelectGameName,
    setGameIdValue,
    setEntityTypeValue,
    gameName,
    regionsCountries = [],
    feedBackParams,
    btnLogKeyMap = dbiBtnLogMes,
    publicTrueDataReportBtnLogKey = '',
  } = props;

  const { isEN, locale, formatMessage } = useIntlExtend();

  const trueDataHeaderLabels = useMemo<ITrueDataHeaderLabels>(() => ({
    gameName: formatMessage({ id: 'dbi_true_data_game_name' }) as string,
    date: formatMessage({ id: 'dbi_date' }) as string,
    dataSource: formatMessage({ id: 'dbi_feedback_source' }) as string,
    metrics: formatMessage({ id: 'dbi_label_metrics' }) as string,
    dataBrainValue: formatMessage({ id: 'dbi_data' }) as string,
    value: formatMessage({ id: 'dbi_value' }) as string,
    release: formatMessage({ id: 'dbi_release' }) as string,
    platform: formatMessage({ id: 'dbi_platform' }) as string,
    region: formatMessage({ id: 'dbi_region' }) as string,
    source: formatMessage({ id: 'dbi_source' }) as string,
    sourceQuality: formatMessage({ id: 'dbi_source_quality' }) as string,
  }), [locale]);

  const userInfo = useSelector((state: IShowAllTrueDataState) => state?.home?.userInfo);

  // 真数接口只按游戏身份和来源查询；边界处剔除父层遗留的分页、排序参数，统一所有真数入口的请求契约。
  const trueDataRequestParams = useMemo(() => ({
    entity_type: feedBackParams?.entity_type ?? entityType,
    game_id: feedBackParams?.game_id ?? gameId,
    page_id: feedBackParams?.page_id ?? '',
  }), [entityType, feedBackParams?.entity_type, feedBackParams?.game_id, feedBackParams?.page_id, gameId]);

  // 主弹窗显示状态
  const [tableFeedBackModalVisible, setTableFeedBackModalVisible] = useState(false);
  const {
    sourceAvailability,
    trueDataMode,
    internalTableFeedBackData,
    internalTableFeedBackDataTotal,
    internalIsTableFeedBackDataLoading,
    setTrueDataMode,
    hasAlineaDrilldownContext,
    reloadSources,
    resetData,
    fetchTrueDataFeedbackHistory: internalFetchTrueDataFeedbackHistory,
  } = useTrueDataFeedbackData({ gameId, feedBackParams: trueDataRequestParams });

  useEffect(() => {
    // URL 下钻只负责打开弹窗，数据请求和参数清理由数据 Hook 统一处理。
    if (hasAlineaDrilldownContext) {
      setTableFeedBackModalVisible(true);
    }
  }, [hasAlineaDrilldownContext]);

  // 来源类型映射
  const sourceTypeMap = useMemo<TTrueDataSourceLabels>(() => {
    return {
      'Public Data': formatMessage({ id: 'dbi_feedback_public_data' }),
      'Expert Knowledge': formatMessage({ id: 'dbi_feedback_expert_knowledge' }),
      'Financial Reports': formatMessage({ id: 'dbi_feedback_financial_reports' }),
      Others: formatMessage({ id: 'dbi_feedback_public_others' }),
    };
  }, [locale]);

  // 监听window的resize调整样式
  const { isOverRightWidth } = useWindowResize();

  // 分页
  const { pageIndex, handlePageChange, setPageIndex } = useTablePagination({
    feedbackParams: trueDataRequestParams,
    fetchTrueDataFeedbackHistory: internalFetchTrueDataFeedbackHistory,
  });

  // 排序
  const { sortConfig, handleSortChange } = useTableSort({
    mode: trueDataMode,
    feedbackParams: trueDataRequestParams,
    fetchTrueDataFeedbackHistory: internalFetchTrueDataFeedbackHistory,
    setPageIndex,
  });

  const { isDownloading, download } = useTrueDataDownload({
    mode: trueDataMode,
    sortConfig,
    headerLabels: trueDataHeaderLabels,
    sourceTypeLabels: sourceTypeMap,
    formatMessage,
    feedBackParams: trueDataRequestParams,
    gameName,
    gameId,
    regionsCountries,
  });

  // 预览图片
  const { isImagePreviewVisible, imageList, openImagePreview, closeImagePreview } = useImagePreview();

  // 删除
  const { isDeleteConfirmVisible, openDeleteConfirm, closeDeleteConfirm, handleDelete } = useDeleteConfirm({
    formatMessage,
    feedbackParams: trueDataRequestParams,
    fetchTrueDataFeedbackHistory: internalFetchTrueDataFeedbackHistory,
  });

  // 编辑
  const {
    isFeedbackModalVisible,
    isEditMode,
    editingRecord,
    openEdit,
    // openAdd,
    closeFeedbackModal,
  } = useFeedbackEdit({
    gameId,
    btnLogKeyMap,
    publicTrueDataReportBtnLogKey,
  });

  // 表格数据格式化
  const { formattedTableData } = useFormattedTableData({
    locale,
    formatMessage,
    tableFeedBackData: internalTableFeedBackData,
  });

  const tableRecords = useMemo<IPublicFeedback[]>(() => {
    // 两种接口字段不同，但都遵循同一条展示规则：同组只渲染首行，组内边框由最后一行保留。
    return markMetricGroups(formattedTableData, (record) => {
      if (trueDataMode === 'alinea') {
        return getAlineaMetricItem(record)?.title || record?.metric || '';
      }

      return record?.metric_name || record?.metric || '';
    });
  }, [formattedTableData, trueDataMode]);

  /**
   * 打开链接
   */
  const handleClickLink = (url: string): void => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const handleSourceChange = (mode: TTrueDataMode): void => {
    if (mode === trueDataMode) {
      return;
    }

    // 仅用户实际点击 Alinea 标签时计数；自动下钻和唯一来源默认选中不应重复上报。
    if (mode === 'alinea') {
      reportButtonLog({
        ...dbiBtnLogMes.trueDataAlineaTabClick,
        extInfo: `mode=alinea&gameId=${gameId}`,
      });
    }
    // 切换数据源后，原分页位置不再对应新来源，必须从第一页开始读取。
    setPageIndex(1);
    resetData();
    setTrueDataMode(mode);
    void internalFetchTrueDataFeedbackHistory({
      ...trueDataRequestParams,
      order_metric: mode === 'alinea' ? 'date' : 'metric_date',
      order: 'desc',
    }, mode);
  };

  const handleDownload = async (): Promise<void> => {
    /** 来源无数据或已有下载任务时拦截，避免下载空文件和重复生成文件。 */
    if (isDownloading || sourceAvailability[trueDataMode] !== 'available') {
      return;
    }

    try {
      await download();
      reportButtonLog({
        ...dbiBtnLogMes.trueDataDownloadClick,
        extInfo: `mode=${trueDataMode}&gameId=${gameId}`,
      });
      notificationSimple(
        'success',
        formatMessage({
          id: 'dbi_download_page_short_waitting_tips',
        }) as string,
      );
    } catch {
      notificationSimple(
        'error',
        formatMessage({ id: 'dbi_download_common_error_tips' }) as string,
      );
    }
  };

  // 表格columns
  const { columns } = useTableColumns({
    isEN,
    mode: trueDataMode,
    locale,
    regionsCountries,
    formatMessage,
    isOverRightWidth,
    sortConfig,
    handleSortChange,
    sourceTypeMap,
    openImagePreview,
    handleClickLink,
    currentUserId: userInfo?.userid,
    openEdit,
    openDeleteConfirm,
  });

  // 两个来源都确认无数据时隐藏入口；弹窗已打开时仍保留空态表格。
  if (sourceAvailability.di === 'empty' && sourceAvailability.alinea === 'empty' && !tableFeedBackModalVisible) {
    return null;
  }

  const canDownload = sourceAvailability[trueDataMode] === 'available';
  const metricColumnKey = trueDataMode === 'alinea' ? 'metric' : 'metric_name';

  return (
    <div style={style} className={classnames('dbi-combine-details-kpi-trends-show-all-data', className)}>
      {/* 显示所有数据按钮 */}
      <Button
        className="dbi-combine-details-kpi-trends-show-all-data__button"
        type="link"
        onClick={(e) => {
          e?.stopPropagation();
          reloadSources();
          setTableFeedBackModalVisible(true);

          const btnLogCfg = dbiBtnLogMes.showAllTrueDataClick;
          reportButtonLog({ ...btnLogCfg, extInfo: `${gameId}: click show all true data` });
        }}
      >
        <span className="dbi-combine-details-kpi-trends-show-all-data__button-text">
          {formatMessage({ id: 'dbi_top_show_all_true_data_text' })}
        </span>
      </Button>

      {/* 数据列表弹窗 */}
      <Modal
        className="dbi-combine-details-kpi-trends-show-all-data-modal"
        size={'87%'}
        visible={tableFeedBackModalVisible}
        onClose={() => {
          closeTipGroup(TRUE_DATA_INFO_TIP_GROUP);
          setTableFeedBackModalVisible(false);
        }}
        caption={
          <div
            className="dbi-combine-details-kpi-trends-show-all-data-modal__title"
            onMouseLeave={() => closeTipGroup(TRUE_DATA_INFO_TIP_GROUP)}
          >
            <span>{formatMessage({ id: 'dbi_detail_true_data' })}</span>
            <InfoIconTip
              tipText={formatMessage({ id: 'dbi_true_data_source_tips' })}
              maxWidth={450}
              groupId={TRUE_DATA_INFO_TIP_GROUP}
            />
          </div>
        }
      >
        <Modal.Body>
          <div className="dbi-combine-details-kpi-trends-show-all-data-modal__toolbar">
            {/* Active Tab 使用白底蓝色图标，非 Active 使用数据源原色图标，避免蓝色按钮上的图标反色错误。 */}
            <div className="dbi-combine-details-kpi-trends-show-all-data-modal__source-tabs">
              <Button
                className={classnames(
                  'dbi-combine-details-kpi-trends-show-all-data-modal__source-tab',
                  {
                    'dbi-combine-details-kpi-trends-show-all-data-modal__source-tab--active':
                      trueDataMode === 'di' && !internalIsTableFeedBackDataLoading,
                  },
                )}
                type={trueDataMode === 'di' && !internalIsTableFeedBackDataLoading ? 'primary' : 'weak'}
                disabled={sourceAvailability.di !== 'available' || internalIsTableFeedBackDataLoading}
                onClick={() => handleSourceChange('di')}
              >
                <span className="dbi-combine-details-kpi-trends-show-all-data-modal__source-icon">
                  <img
                    src={getDataSourceIcon(
                      trueDataMode === 'di' && !internalIsTableFeedBackDataLoading
                        ? 'databrainbgresized'
                        : 'databrainbluebg',
                    )}
                    alt=""
                  />
                </span>
                DataBrain
              </Button>
              <Button
                className={classnames(
                  'dbi-combine-details-kpi-trends-show-all-data-modal__source-tab',
                  {
                    'dbi-combine-details-kpi-trends-show-all-data-modal__source-tab--active':
                      trueDataMode === 'alinea' && !internalIsTableFeedBackDataLoading,
                  },
                )}
                type={trueDataMode === 'alinea' && !internalIsTableFeedBackDataLoading ? 'primary' : 'weak'}
                disabled={sourceAvailability.alinea !== 'available' || internalIsTableFeedBackDataLoading}
                onClick={() => handleSourceChange('alinea')}
              >
                <span className="dbi-combine-details-kpi-trends-show-all-data-modal__source-icon">
                  <img
                    src={
                      trueDataMode === 'alinea' && !internalIsTableFeedBackDataLoading
                        ? AlineaActiveIcon
                        : getDataSourceIcon('alinea')
                    }
                    alt=""
                  />
                </span>
                Alinea Analytics
              </Button>
            </div>
            <Button
              className="dbi-combine-details-kpi-trends-show-all-data-modal__download"
              type="link"
              disabled={!canDownload}
              loading={isDownloading}
              onClick={() => void handleDownload()}
            >
              <SvgIcon type="download" />
              {formatMessage({ id: 'dbi_true_data_download' })}
            </Button>
          </div>
          {internalIsTableFeedBackDataLoading && (
            <div className="dbi-combine-details-kpi-trends-show-all-data-modal__loading">
              <Loading content={formatMessage({ id: 'common_loading' })} />
            </div>
          )}
          {!internalIsTableFeedBackDataLoading && (
            <Table
              className="dbi-combine-details-kpi-trends-show-all-data-modal__table"
              records={tableRecords}
              columns={columns}
              addons={[
                injectable({
                  cell: (props, { column, recordIndex, record }) => {
                    // 仅 Metrics 数据单元格需要定制；表头和其他列继续沿用 Tea Table 默认样式。
                    if (recordIndex === -1 || column.key !== metricColumnKey) {
                      return props;
                    }
                    return {
                      ...props,
                      className: classnames(
                        props.className,
                        'dbi-combine-details-kpi-trends-show-all-data-modal__metric-cell',
                        {
                          'dbi-combine-details-kpi-trends-show-all-data-modal__metric-cell--continuation':
                            record?.is_metric_last === false,
                        },
                      ),
                    };
                  },
                }),
                // 不设置固定 minWidth，让表格随弹窗宽度收缩，小屏通过换行展示而不是强制横向滚动。
                scrollable({}),
                autotip({ emptyText: formatMessage({ id: 'dbi_detail_no_data_under' }) }),
                pageable({
                  stateTextVisible: internalTableFeedBackDataTotal > 0,
                  stateText: formatMessage(
                    { id: 'dbi_pagination_total' },
                    { total: internalTableFeedBackDataTotal ?? 0 },
                  ),
                  pageIndexVisible: internalTableFeedBackDataTotal > PAGE_SIZE,
                  pageSizeVisible: false,
                  jumpVisible: internalTableFeedBackDataTotal > PAGE_SIZE,
                  endJumpVisible: internalTableFeedBackDataTotal > PAGE_SIZE,
                  pageSizeOptions: [PAGE_SIZE],
                  recordCount: internalTableFeedBackDataTotal,
                  pageIndex,
                  onPagingChange: handlePageChange,
                }),
              ]}
            />
          )}
        </Modal.Body>
        {/* <Modal.Footer>
          <Button
            onClick={() => {
              setTableFeedBackModalVisible(false);
            }}
          >
            {formatMessage({ id: 'dbi_close' })}
          </Button>
        </Modal.Footer> */}
      </Modal>

      {/* 反馈编辑/新增弹窗 */}
      {isFeedbackModalVisible && (
        <PublicTrueData
          pageId={pageId}
          entityType={entityType}
          gameId={gameId}
          btnLogKeyMap={btnLogKeyMap}
          publicTrueDataReportBtnLogKey={publicTrueDataReportBtnLogKey}
          isOutsideOpen={true}
          isNeedSearchGame={true}
          isGameDetail={true}
          buttonNoShow={true}
          isEdit={isEditMode}
          match={{ ...props?.match, entityType }}
          editRecord={editingRecord}
          feedBackParams={feedBackParams}
          fetchTrueDataFeedbackHistory={internalFetchTrueDataFeedbackHistory}
          outsideFeedBackVisible={isFeedbackModalVisible}
          setOutsideFeedBackVisible={closeFeedbackModal}
          gameData={gameData}
          setGameData={setGameData}
          selectGameName={selectGameName}
          setSelectGameName={setSelectGameName}
          setGameIdValue={setGameIdValue}
          setEntityTypeValue={setEntityTypeValue}
        />
      )}

      {/* 图片预览弹窗 */}
      {isImagePreviewVisible && (
        <ImagePreview visible={isImagePreviewVisible} imageList={imageList} onClose={closeImagePreview} />
      )}

      {/* 删除确认弹窗 */}
      {isDeleteConfirmVisible && (
        <Modal
          className="dbi-combine-details-kpi-trends-show-all-data-delete-confirm-modal"
          size={500}
          visible={isDeleteConfirmVisible}
          onClose={closeDeleteConfirm}
        >
          <Modal.Body>
            <div>{formatMessage({ id: 'dbi_feedback_you_sure_delete_true_data' })}</div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="primary"
              onClick={(e) => {
                e?.stopPropagation();
                void handleDelete();
              }}
            >
              {formatMessage({ id: 'dbi_confirm' })}
            </Button>
            <Button type="weak" onClick={closeDeleteConfirm}>
              {formatMessage({ id: 'dbi_cancel' })}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default ShowAllTrueData;
